import React, { useState, useRef, useEffect } from 'react';
import md5 from 'md5';
import { useSelector } from 'react-redux';
import { useParams, useLocation } from 'react-router-dom';
import useSWR from 'swr';
import { Button, Card, Modal, Spinner, Alert } from 'react-bootstrap';
import styles from '../../../styles.module.css';
import useViewFiles from '../../../hooks/useViewFiles';
import { getPrefix } from '../../../util/utils';
import FileCarousel from '../../../components/laudos/FileCarousel';
import LaudoCardHeader from '../../../components/laudos/LaudoCardHeader';
import IcmHeaderImage from '../../../components/IcmHeaderImage';
import selectEcoById from '../../../services/ecodoppler';
import useIsMedicoExterno from '../../../hooks/useIsMedicoExterno';
import generateDopplerPDF from './generateDopplerPDF';

const renderResumoSection = (title, rows) => {
  if (!rows || rows.length === 0) return null;
  return (
    <div className="avoid-break mt-1">
      <div style={{ fontWeight: 700, textDecoration: 'underline' }}>
        {title}:
      </div>
      {rows.map((row, idx) => (
        <div key={`${title}-${idx}`}>{row}</div>
      ))}
    </div>
  );
};

const renderDottedMetric = ({ label, value, unit }) => (
  <div key={`metric-${label}`} className="d-flex align-items-end">
    <span>{label}</span>
    <span
      style={{
        flex: 1,
        borderBottom: '1px dotted #444',
        margin: '0 6px 3px 6px',
      }}
    />
    <span>{value}</span>
    {unit ? <span style={{ marginLeft: 6 }}>{unit}</span> : null}
  </div>
);

const resumoList = list =>
  list?.map(item => item?.DESCRICAO).filter(Boolean) || [];

const ReportHeader = ({ printing }) => (
  <div className={styles.imageReport}>
    <IcmHeaderImage variant="print" hidden={!printing} />
  </div>
);

export default function ReportDoppler() {
  const { id } = useParams();
  const location = useLocation();
  const { printing = false } = location.state || {};
  const { data: eco, error, isLoading } = useSWR(id ? ['eco', id] : null, () =>
    selectEcoById(id)
  );
  const isPaciente = useSelector(state => Boolean(state.patient?.signed));
  const isMedicoExterno = useIsMedicoExterno() || isPaciente;
  const [cpfPaciente, setcpfPaciente] = useState(false);
  const {
    urls,
    setURLs,
    loadingFiles,
    showModal,
    setShowModal,
    loadFiles,
  } = useViewFiles({ codLaudo: eco?.CODECO, procedimento: 'eco' });
  const [isResumoConvenio, setIsResumoConvenio] = useState(false);
  const [isTimbradoPrint, setIsTimbradoPrint] = useState(printing);
  const prefixoSolicitou = getPrefix(eco?.CODSOLICITANTE);
  const alteradoRef = useRef(null);
  const normalRef = useRef(null);
  const resumoRef = useRef(null);
  /** Evita mutar fontSize no DOM fora do React (corrige removeChild vs reconciler). */
  const [laudoFontSizePx, setLaudoFontSizePx] = useState(null);
  const [pageBreakA, setPageBreakA] = useState(false);
  const [pageBreakB, setPageBreakB] = useState(false);
  const [pageBreakC, setPageBreakC] = useState(false);

  const resumoMetricRows = [
    {
      label: 'Septo',
      value: eco?.SEPTO != null ? Number(eco.SEPTO).toFixed(1) : null,
      unit: '(cm)',
    },
    {
      label: 'PP VE',
      value: eco?.PPVE != null ? Number(eco.PPVE).toFixed(1) : null,
      unit: '(cm)',
    },
    {
      label: 'Diâmetro Diastólico',
      value: eco?.DD != null ? Number(eco.DD).toFixed(1) : null,
      unit: '(cm)',
    },
    {
      label: 'Diâmetro Sistólico',
      value: eco?.DS != null ? Number(eco.DS).toFixed(1) : null,
      unit: '(cm)',
    },
    {
      label: 'Relação Espessura/Volume',
      value: eco?.avaliaVSVE != null ? Number(eco.avaliaVSVE).toFixed(2) : null,
      unit: '',
    },
    {
      label: 'Fração de Ejeção (Monoplanar)',
      value: eco?.fracaoEjecaoMono != null ? `${eco.fracaoEjecaoMono}` : null,
      unit: '',
    },
  ].filter(item => item.value != null);

  const handleRemoveDiv = () => {
    window.scrollTo(0, 0);
    window.print();
  };

  const handlePageBreak = option => {
    if (option === 'A') setPageBreakA(prev => !prev);
    if (option === 'B') setPageBreakB(prev => !prev);
    if (option === 'C') setPageBreakC(prev => !prev);
  };

  const getLaudoFontRootElement = () => {
    if (isResumoConvenio) return resumoRef.current;
    if (eco?.TIPOLAUDO === 'Alterado') return alteradoRef.current;
    return normalRef.current;
  };

  const handleIncreaseFont = () => {
    const el = getLaudoFontRootElement();
    if (!el) return;

    setLaudoFontSizePx(prev => {
      const base = prev ?? parseFloat(window.getComputedStyle(el).fontSize);
      if (Number.isNaN(base)) return prev;
      return base + 1;
    });
  };

  const handleDecreaseFont = () => {
    const el = getLaudoFontRootElement();
    if (!el) return;

    setLaudoFontSizePx(prev => {
      const base = prev ?? parseFloat(window.getComputedStyle(el).fontSize);
      if (Number.isNaN(base)) return prev;
      return Math.max(8, base - 1);
    });
  };

  useEffect(() => {
    if (!eco?.CODECO) return;
    loadFiles({ openModal: false, toastOnError: false });
  }, [eco?.CODECO, loadFiles]);

  useEffect(() => {
    setLaudoFontSizePx(null);
  }, [eco?.CODECO, eco?.TIPOLAUDO, isResumoConvenio]);

  useEffect(() => {
    if (isMedicoExterno) {
      setIsTimbradoPrint(true);
      return;
    }
    setIsTimbradoPrint(printing);
  }, [printing, isMedicoExterno]);

  const showTimbradoPrint = isMedicoExterno || isTimbradoPrint;

  const laudoFontStyle =
    laudoFontSizePx != null ? { fontSize: `${laudoFontSizePx}px` } : undefined;

  const observacoesResumo = eco?.OBSERVACOES?.trim()
    ? eco.OBSERVACOES.trim().slice(0, 1200)
    : '';

  const resumoConclusoes =
    eco?.conclusoes?.map(item => item?.conclusoes).filter(Boolean) || [];

  if (error) {
    return (
      <Alert variant="danger" style={{ marginTop: 10 }}>
        <Alert.Heading>Erro ao carregar laudo de eco!</Alert.Heading>
        <p>Tente novamente.</p>
      </Alert>
    );
  }

  if (isLoading || !eco) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '80vh',
        }}
      >
        <Spinner animation="border" variant="dark" size="lg" />
      </div>
    );
  }

  const hasProteseMitral =
    eco?.GMPM != null || (eco?.refluxoPM && eco.refluxoPM.length > 0);
  const hasValvaMitral = eco?.valvaMitral && eco.valvaMitral.length > 0;
  const hasProteseAortica =
    eco?.GMPAO != null || (eco?.refluxoPAO && eco.refluxoPAO.length > 0);
  const hasValvaAortica =
    eco?.DAVAO !== null || (eco?.valvaAO && eco.valvaAO.length > 0);
  const hasProteseTricuspide =
    eco?.GMPT != null || (eco?.refluxoPT && eco.refluxoPT.length > 0);
  const hasValvaTricuspide = eco?.valvaTricu && eco.valvaTricu.length > 0;
  const hasProtesePulmonar =
    eco?.GMPP != null || (eco?.refluxoPP && eco.refluxoPP.length > 0);
  const hasValvaPulmonar =
    eco?.DAVP != null || (eco?.valvaPulmo && eco.valvaPulmo.length > 0);

  return (
    <>
      <style>
        {`@media print {
          @page {
            margin-top:${showTimbradoPrint ? '0.8cm' : '4.0cm'};
            margin-bottom: 3cm;
          }

          .timbradoHeaderPrint {
            margin-top: 0 !important;
            margin-bottom: 0.5cm !important;
          }

          .removeMePrint {
            display: none !important;
          }

          .page-break {
            page-break-before: always;
            break-before: always;
          }

          #alterado {
            font-size: 1.2em;
          }

          #alterado * {
            font-size: inherit;
          }

          #normal {
            font-size: 1.2em;
          }

          #normal * {
            font-size: inherit;
          }

          #resumoConvenio {
            font-size: 0.9rem;
            line-height: 1.2;
          }

          #resumoConvenio .avoid-break {
            break-inside: avoid-page;
            page-break-inside: avoid;
          }
        }`}
      </style>

      <Card className="mb-2">
        <div translate="no">
          {!isMedicoExterno && (
            <div className="d-flex justify-content-end removeMePrint">
              <Button
                size="sm"
                variant="outline-primary"
                onClick={handleIncreaseFont}
                aria-label="Aumentar fonte"
              >
                A+
              </Button>
              <Button
                size="sm"
                variant="outline-primary"
                onClick={handleDecreaseFont}
                aria-label="Diminuir fonte"
              >
                A-
              </Button>
            </div>
          )}

          {showTimbradoPrint && (
            <div
              className={`${styles.imageReport} timbradoHeaderPrint`}
              style={{ marginTop: 0 }}
            >
              <IcmHeaderImage variant="print" />
            </div>
          )}
          <LaudoCardHeader title="ECOCARDIOGRAMA DOPPLER" />
          {!isMedicoExterno && (
            <Card.Body className="mb-2 removeMePrint">
              <div className="mb-0">
                <label className="form-label font-weight-bold">
                  Onde deseja a quebra de página?
                </label>
                <div className="d-flex flex-wrap gap-3 justify-content-left align-items-left">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={pageBreakA}
                      onChange={() => handlePageBreak('A')}
                      id="breakA"
                    />
                    <label className="form-check-label" htmlFor="breakA">
                      Valva Mitral
                    </label>
                  </div>
                  <div className="ml-3 form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={pageBreakB}
                      onChange={() => handlePageBreak('B')}
                      id="breakB"
                    />
                    <label className="form-check-label" htmlFor="breakB">
                      Valva Aórtica
                    </label>
                  </div>
                  <div className="ml-3 form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={pageBreakC}
                      onChange={() => handlePageBreak('C')}
                      id="breakC"
                    />
                    <label className="form-check-label" htmlFor="breakC">
                      Valva Tricúspide
                    </label>
                  </div>
                </div>
              </div>{' '}
              <div>
                <label className="font-weight-bold">
                  Imprimir CPF do Paciente?
                </label>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="cpfPaciente"
                    value="SIM"
                    checked={cpfPaciente === true}
                    onChange={() => setcpfPaciente(true)}
                  />
                  <label
                    className="form-check-label mr-5"
                    htmlFor="cpfPacienteSim"
                  >
                    Sim
                  </label>
                </div>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="cpfPaciente"
                    value="NAO"
                    checked={cpfPaciente === false}
                    onChange={() => setcpfPaciente(false)}
                  />
                  <label className="form-check-label" htmlFor="cpfPacienteNao">
                    Não
                  </label>
                </div>
              </div>
            </Card.Body>
          )}
          <Card.Body>
            {isResumoConvenio ? (
              <div id="resumoConvenio" ref={resumoRef} style={laudoFontStyle}>
                <div
                  className="avoid-break mb-2"
                  style={{ textAlign: 'center' }}
                >
                  <div style={{ fontWeight: 700, textDecoration: 'underline' }}>
                    ICM - Instituto do Coração de Marília
                  </div>
                  <div style={{ fontWeight: 700 }}>Ecocardiograma - Resumo</div>
                </div>

                <hr style={{ margin: '8px 0', borderTop: '1px solid #999' }} />

                <div className="avoid-break mb-2">
                  <div>
                    <strong>Paciente:</strong> {eco?.CODPAC} &nbsp;&nbsp;
                    {eco?.NOMEPAC}
                  </div>
                  <div>
                    <strong>Sexo:</strong> {eco?.Sexo} &nbsp;&nbsp;
                    <strong>Dt Nasc:</strong> {eco?.dataNascFmt} &nbsp;&nbsp;
                    <strong>Idade:</strong> {eco?.idadePAC} anos
                  </div>
                  <div>
                    {eco?.PESOPAC && (
                      <>
                        <strong>Peso:</strong> {eco?.PESOPAC} Kg &nbsp;&nbsp;
                      </>
                    )}
                    {eco?.ALTURAPAC && (
                      <>
                        <strong>Altura:</strong> {eco?.ALTURAPAC} m &nbsp;&nbsp;
                      </>
                    )}
                    {eco?.FC && (
                      <>
                        <strong>FC:</strong> {eco?.FC} bpm &nbsp;&nbsp;
                      </>
                    )}
                    <strong>Dt Realização:</strong> {eco?.dataEcoFmt}
                  </div>
                  <div>
                    <strong>Convênio:</strong> {eco?.SIGLACONV} &nbsp;&nbsp;
                    <strong>Laudo:</strong> {eco?.CODECO} &nbsp;&nbsp;
                    <strong>Dt Laudo:</strong> {eco?.dataEcoFmt}
                    {cpfPaciente ? `  |  CPF: ${eco?.CPFPAC}` : ''}
                  </div>
                  {/* <div>
                    <strong>Médico que Realizou:</strong> {prefixoRealizou} {eco?.Realizou}
                  </div> */}
                  <div>
                    <strong>Médico que Solicitou:</strong> {prefixoSolicitou}{' '}
                    {eco?.Solicitou}
                  </div>
                </div>

                <hr style={{ margin: '8px 0', borderTop: '1px solid #999' }} />

                {resumoMetricRows.length > 0 && (
                  <div className="avoid-break mb-2">
                    <div style={{ fontWeight: 700, fontSize: '1.05rem' }}>
                      Diâmetros e Função Sistólica do Ventrículo Esquerdo
                    </div>
                    <div className="row">
                      <div className="col-7">
                        {resumoMetricRows
                          .filter(
                            item =>
                              item.label !== 'Fração de Ejeção (Monoplanar)'
                          )
                          .map(item => renderDottedMetric(item))}
                      </div>
                      <div className="col-5">
                        {resumoMetricRows
                          .filter(
                            item =>
                              item.label === 'Fração de Ejeção (Monoplanar)'
                          )
                          .map(item => renderDottedMetric(item))}
                      </div>
                    </div>
                  </div>
                )}

                {renderResumoSection(
                  'Achados Descritivos do VE',
                  resumoList(eco?.achados)
                )}
                {renderResumoSection(
                  'Contração Segmentar',
                  eco?.contraRegiao
                    ?.map(item => item?.CONTRASEG)
                    .filter(Boolean)
                )}
                <div
                  className="avoid-break mt-1"
                  style={{ fontWeight: 700, textDecoration: 'underline' }}
                >
                  Avaliação da Função Diastólica do Ventrículo Esquerdo
                </div>

                {renderResumoSection('Átrio Esquerdo e Valva Mitral', [
                  ...(eco?.DAE != null
                    ? [
                      `Diâmetro do Átrio Esquerdo: ${Number(eco.DAE).toFixed(
                        1
                      )} (cm)`,
                    ]
                    : []),
                  ...resumoList(eco?.atrioEsq),
                  ...resumoList(eco?.valvaMitral),
                ])}

                {renderResumoSection('Valva Aórtica', [
                  ...(eco?.DAVAO != null
                    ? [
                      `Diâmetro da Raiz da Aorta: ${Number(eco.DAVAO).toFixed(
                        1
                      )} (cm)`,
                    ]
                    : []),
                  ...resumoList(eco?.valvaAO),
                ])}

                {renderResumoSection('Aorta', resumoList(eco?.aorta))}
                {renderResumoSection(
                  'Átrio Direito',
                  resumoList(eco?.atrioDir)
                )}
                {renderResumoSection(
                  'Veia Cava Inferior',
                  resumoList(eco?.veiaCavaInf)
                )}
                {renderResumoSection(
                  'Ventrículo Direito',
                  resumoList(eco?.ventriDir)
                )}
                {renderResumoSection(
                  'Valva Tricúspide',
                  resumoList(eco?.valvaTricu)
                )}
                {renderResumoSection(
                  'Valva Pulmonar',
                  resumoList(eco?.valvaPulmo)
                )}
                {renderResumoSection('Pericárdio', resumoList(eco?.pericardio))}

                {observacoesResumo && (
                  <div className="avoid-break mt-1">
                    <div
                      style={{ fontWeight: 700, textDecoration: 'underline' }}
                    >
                      Observações:
                    </div>
                    <pre
                      className={styles.preContent}
                      style={{ fontSize: 14, marginBottom: 0 }}
                    >
                      {observacoesResumo}
                    </pre>
                  </div>
                )}

                {resumoConclusoes.length > 0 && (
                  <div className="avoid-break mt-1">
                    <div
                      style={{ fontWeight: 700, textDecoration: 'underline' }}
                    >
                      Conclusões:
                    </div>
                    {resumoConclusoes.map((conclusao, idx) => (
                      <div key={`conclusao-resumo-${idx}`}>{conclusao}</div>
                    ))}
                  </div>
                )}

                <div className="avoid-break mt-2">
                  <i>Conferido e assinado eletronicamente por:</i>{' '}
                  <strong>
                    {getPrefix(eco?.CODREALIZOU)} {eco?.Realizou}
                  </strong>
                  <br />
                  <i>Assinatura Digital: {md5(JSON.stringify(eco))}</i>
                </div>
              </div>
            ) : eco && eco?.TIPOLAUDO === 'Alterado' ? (
              <div id="alterado" ref={alteradoRef} style={laudoFontStyle}>
                <strong className="mr-auto">Nome do Paciente: </strong>
                {`${eco?.CODPAC} - ${eco?.NOMEPAC}`}
                {cpfPaciente ? ` - CPF: ${eco?.CPFPAC}` : ''}
                <br />
                <strong className="mr-auto">Data de Nasc: </strong>
                {eco?.dataNascFmt}&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                <strong className="mr-auto">Sexo: </strong>
                {eco?.Sexo}&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                <strong className="mr-auto">Idade: </strong>
                {eco?.idadePAC} anos&nbsp;&nbsp;&nbsp;
                <br />
                <div className="d-flex flex-wrap align-items-center gap-2">
                  {eco?.PESOPAC && (
                    <>
                      <strong className="mr-2">Peso:</strong>
                      <span>{eco.PESOPAC} Kg&nbsp;&nbsp;&nbsp;&nbsp;</span>
                    </>
                  )}

                  {eco?.ALTURAPAC && (
                    <>
                      <strong className="mr-2">Altura:</strong>
                      <span>{eco.ALTURAPAC} m&nbsp;&nbsp;&nbsp;&nbsp;</span>
                    </>
                  )}

                  {eco?.FC && (
                    <>
                      <strong className="mr-2">FC:&nbsp;</strong>
                      <span>{eco.FC} bpm&nbsp;&nbsp;&nbsp;&nbsp;</span>
                    </>
                  )}

                  {eco?.dataEcoFmt && eco?.FC && (
                    <>
                      <strong className="mr-2">Data de Realização:</strong>
                      <span>{eco.dataEcoFmt}</span>
                    </>
                  )}
                </div>
                <strong className="mr-auto">Convênio: </strong>
                {eco?.SIGLACONV}&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                <strong className="mr-auto">Cod Laudo: </strong>
                {eco?.CODECO}
                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                <strong className="mr-auto">Data do Exame: </strong>
                {eco?.dataEcoFmt}
                {/* <br />
                <strong className="mr-auto">
                  Médico que Realizou: {prefixoRealizou}{' '}
                </strong>
                {eco?.Realizou} */}
                <br />
                <strong className="mr-auto">
                  Médico que Solicitou: {prefixoSolicitou}{' '}
                </strong>
                {eco?.Solicitou}
                <hr />
                {/* 01 */}
                {eco?.obs && (
                  <>
                    {eco.obs.map((obs, index) => (
                      <div className="row" key={index}>
                        <div className="col-12"> - {obs.DESCRICAO}</div>
                      </div>
                    ))}
                    <br />
                  </>
                )}
                {/* 02 */}
                <strong className="mr-auto">
                  Diâmetros e Função Sistólica do Ventrículo Esquerdo{' '}
                </strong>
                <div className="row">
                  {/* Coluna Esquerda */}
                  <div className="col-6">
                    <ul className="list-unstyled m-0">
                      {eco?.SEPTO && (
                        <li>{`- Septo: ${eco?.SEPTO != null
                          ? Number(eco.SEPTO).toFixed(1)
                          : '-'
                          } (cm)`}</li>
                      )}
                      {eco?.PPVE && (
                        <li>{`- PPVE: ${eco?.PPVE != null ? Number(eco.PPVE).toFixed(1) : '-'
                          } (cm)`}</li>
                      )}
                      {eco?.DD && (
                        <li>{`- Diâmetro Diastólico: ${eco?.DD != null ? Number(eco.DD).toFixed(1) : '-'
                          } (cm)`}</li>
                      )}
                      {eco?.DS && (
                        <li>{`- Diâmetro Sistólico: ${Number(eco.DS).toFixed(
                          1
                        )} (cm)`}</li>
                      )}
                      {eco?.avaliaVSVE && (
                        <li>{`- Relação Espessura/Volume: ${Number(
                          eco.avaliaVSVE
                        ).toFixed(2)}`}</li>
                      )}
                      {eco?.massaVE && (
                        <li>{`- Massa do VE: ${Math.floor(
                          eco.massaVE
                        )} (g/m²)`}</li>
                      )}
                    </ul>
                  </div>

                  {/* Coluna Direita */}
                  {eco?.SEPTO ||
                    eco?.PPVE ||
                    eco?.DD ||
                    eco?.DS ||
                    eco?.avaliaVSVE ||
                    eco?.massaVE ? (
                    <div className="col-6">
                      <ul className="list-unstyled m-0">
                        {eco?.fracaoEjecaoMono && (
                          <li>
                            <strong>- Fração de Ejeção (Monoplanar): </strong>
                            {`${eco.fracaoEjecaoMono}`}
                          </li>
                        )}
                        {eco?.FRAEJECBIPLA && (
                          <li>
                            <strong>- Fração de Ejeção (Simpson): </strong>
                            {`${Number(eco.FRAEJECBIPLA).toFixed(2)}`}
                          </li>
                        )}
                        {eco?.FRAEJE3D != null && (
                          <li>
                            <strong>- Fração de Ejeção (3D): </strong>
                            {`${Number(eco.FRAEJE3D).toFixed(2)}`}
                          </li>
                        )}
                      </ul>
                    </div>
                  ) : (
                    <div className="col-12">
                      <ul className="list-unstyled m-0">
                        {eco?.fracaoEjecaoMono && (
                          <li>
                            <strong>- Fração de Ejeção (Monoplanar): </strong>
                            {`${eco.fracaoEjecaoMono}`}
                          </li>
                        )}
                        {eco?.FRAEJECBIPLA && (
                          <li>
                            <strong>- Fração de Ejeção (Simpson): </strong>
                            {`${Number(eco.FRAEJECBIPLA).toFixed(2)}`}
                          </li>
                        )}
                        {eco?.FRAEJE3D != null && (
                          <li>
                            <strong>- Fração de Ejeção (3D): </strong>
                            {`${Number(eco.FRAEJE3D).toFixed(2)}`}
                          </li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>
                <br />
                {/* 03 */}
                {eco?.achados?.length > 0 && (
                  <>
                    <u className="mr-auto">Achados Descritivos do VE: </u>
                    {eco.achados.map(achado => (
                      <div key={`achado-${achado.DESCRICAO}`} className="row">
                        <div className="col-12"> - {achado.DESCRICAO}</div>
                      </div>
                    ))}
                    <br />
                  </>
                )}
                {/* 04 */}
                {eco?.contraRegiao?.length > 0 && (
                  <>
                    <u className="mr-auto">Contração Segmentar: </u>
                    {eco.contraRegiao.map(contra => (
                      <div key={`contra-${contra.CONTRASEG}`} className="row">
                        <div className="col-12"> - {contra.CONTRASEG}</div>
                      </div>
                    ))}
                    <br />
                  </>
                )}
                {/* 05 */}
                {eco?.avDiast.length > 0 ||
                  eco?.indicePM ||
                  eco?.estimativaDC ? (
                  <div>
                    <strong className="mr-auto">
                      Avaliação da Função Diastólica do Ventrículo Esquerdo{' '}
                    </strong>
                  </div>
                ) : null}
                {eco?.indicePM && !isNaN(eco?.indicePM) && (
                  <div className="row">
                    <div className="col-12">{`- Índice de Performance: ${Number(
                      eco.indicePM
                    ).toFixed(2)} (Normal < 0.42)`}</div>
                  </div>
                )}
                {eco?.estimativaDC && !isNaN(eco?.estimativaDC) && (
                  <div className="row">
                    <div className="col-12">{`- Estimativa do Débito Cardíaco: ${eco.estimativaDC} (l/min/m2) (2.4 a 4.2)`}</div>
                  </div>
                )}
                {eco?.avDiast?.length > 0 && (
                  <>
                    {eco.avDiast.map(avDiast => (
                      <div key={`avDiast-${avDiast.DESCRICAO}`} className="row">
                        <div className="col-12"> - {avDiast.DESCRICAO}</div>
                      </div>
                    ))}
                    <br />
                  </>
                )}
                {/* 06 */}
                {eco?.ventriDir?.length > 0 && (
                  <>
                    <strong className="mr-auto">Ventrículo Direito</strong>
                    {eco.ventriDir.map(ventriDir => (
                      <div
                        key={`ventriDir-${ventriDir.DESCRICAO}`}
                        className="row"
                      >
                        <div className="col-12"> - {ventriDir.DESCRICAO}</div>
                      </div>
                    ))}
                    <br />
                  </>
                )}
                {/* 07 */}
                <strong className="mr-auto">Átrio Esquerdo</strong>
                {eco?.DAE !== null && (
                  <div className="row">
                    <div className="col-12">{`- Diâmetro do Átrio Esquerdo: ${Number(
                      eco.DAE
                    ).toFixed(1)} (cm)`}</div>
                  </div>
                )}
                {eco?.VOL_AE !== null && (
                  <div className="row">
                    <div className="col-12">{`- Volume do AE: ${Number(
                      eco.VOL_AE
                    ).toFixed()} (cc/m2) (até 34)`}</div>
                  </div>
                )}
                {eco?.atrioEsq?.length > 0 && (
                  <>
                    {eco.atrioEsq.map(atrioEsq => (
                      <div
                        key={`atrioEsq-${atrioEsq.DESCRICAO}`}
                        className="row"
                      >
                        <div className="col-12"> - {atrioEsq.DESCRICAO}</div>
                      </div>
                    ))}
                    <br />
                  </>
                )}
                {/* 08 */}
                {eco?.atrioDir?.length > 0 && (
                  <>
                    <strong className="mr-auto">Átrio Direito</strong>
                    {eco.atrioDir.map(atrioDir => (
                      <div
                        key={`atrioDir-${atrioDir.DESCRICAO}`}
                        className="row"
                      >
                        <div className="col-12"> - {atrioDir.DESCRICAO}</div>
                      </div>
                    ))}
                    <br />
                  </>
                )}
                {/* 09 */}
                {/* Quebra de página aqui */}
                {pageBreakA && (
                  <div className="page-break">
                    <ReportHeader printing={showTimbradoPrint} />
                  </div>
                )}
                {hasProteseMitral && (
                  <strong className="mr-auto">Prótese Mitral</strong>
                )}
                {!hasProteseMitral && hasValvaMitral && (
                  <strong className="mr-auto">Valva Mitral</strong>
                )}
                {/* 09-1 */}
                <div className="row">
                  {eco?.AVM !== null && (
                    <div className="col-12">{`- Área Valva Mitral: ${Number(
                      eco.AVM
                    ).toFixed(1)} (cm2) ${eco.valorAVM || ''}`}</div>
                  )}
                  {eco?.ORIFICIO_VM !== null && (
                    <div className="col-12">{`- Orifício Regurgitação Mitral (PISA): ${Number(
                      eco.ORIFICIO_VM
                    ).toFixed(2)} (cm2)`}</div>
                  )}
                </div>
                <div className="row">
                  {eco?.GMVM !== null && (
                    <div className="col-12">{`- Gradiente Médio: ${eco.GMVM} (mmHg)`}</div>
                  )}
                  {eco?.VOLUME_VM !== null && (
                    <div className="col-12">{`- Volume Regurgitação Mitral: ${eco.VOLUME_VM} (cc)`}</div>
                  )}
                </div>
                {eco.valvaMitral.map(valvaMitral => (
                  <div
                    key={`valvaMitral-${valvaMitral.DESCRICAO}`}
                    className="row"
                  >
                    <div className="col-12"> - {valvaMitral.DESCRICAO}</div>
                  </div>
                ))}
                {eco?.refVM && eco.refVM.length > 0 && (
                  <>
                    {eco.refVM.map((refVM, index) => (
                      <div key={`refVM-${index}`} className="row">
                        <div className="col-12"> - {refVM.DESCRICAO}</div>
                      </div>
                    ))}
                    <br />
                  </>
                )}
                {/* 09-2 */}
                {eco?.GMPM !== null && (
                  <div className="row">
                    <div className="col-12">{`- Gradiente Médio da Prótese Mitral: ${eco.GMPM} (mmHg)`}</div>
                  </div>
                )}
                {eco?.refluxoPM && eco.refluxoPM.length > 0 && (
                  <>
                    {eco.refluxoPM.map((refluxoPM, index) => (
                      <div key={`refluxoPM-${index}`} className="row">
                        <div className="col-12"> - {refluxoPM.refluxoPM}</div>
                      </div>
                    ))}
                    <br />
                  </>
                )}
                {/* 10 */}
                {/* Quebra de página aqui */}
                {pageBreakB && (
                  <div className="page-break">
                    <ReportHeader printing={showTimbradoPrint} />
                  </div>
                )}
                {hasProteseAortica && (
                  <strong className="mr-auto">Prótese Aórtica</strong>
                )}
                {!hasProteseAortica && hasValvaAortica && (
                  <div className="row">
                    <div className="col-12">
                      <strong className="mr-auto">Valva Aórtica</strong>
                    </div>
                  </div>
                )}
                {eco?.DAVAO !== null && (
                  <div className="row">
                    <div className="col-12">{`- Diâmetro da Raiz Aórtica: ${Number(
                      eco?.DAVAO
                    ).toFixed(1)} (cm)`}</div>
                  </div>
                )}
                {eco?.AREA_AO != null && (
                  <div className="row">
                    <div className="col-12">{`- Área Valva Aórtica: ${Number(
                      eco.AREA_AO
                    ).toFixed(2)} (cm2) ${eco.valorAreaAVAO}`}</div>
                  </div>
                )}
                {eco?.GMVAO != null && (
                  <div className="row">
                    <div className="col-12">{`- Gradiente Médio da Valva Aórtica: ${eco.GMVAO} (mmHg)`}</div>
                  </div>
                )}
                {eco?.GMVSVE != null && (
                  <div className="row">
                    <div className="col-12">{`- Gradiente Médio Via de Saída do VE: ${eco.GMVSVE} (mmHg)`}</div>
                  </div>
                )}
                {eco?.valvaAO?.length > 0 && (
                  <>
                    {eco.valvaAO.map((valvaAO, index) => (
                      <div key={`valvaAO-${index}`} className="row">
                        <div className="col-12"> - {valvaAO.DESCRICAO}</div>
                      </div>
                    ))}
                    {/*  <br /> */}
                  </>
                )}
                {eco?.refVAO?.length > 0 && (
                  <>
                    {eco.refVAO.map((refVAO, index) => (
                      <div key={`refVAO-${index}`} className="row">
                        <div className="col-12"> - {refVAO.DESCRICAO}</div>
                      </div>
                    ))}
                    <br />
                  </>
                )}
                {/* 10-2 */}
                {eco?.GMPAO != null && (
                  <div className="row">
                    <div className="col-12">{`- Gradiente Médio da Prótese Aórtica: ${eco.GMPAO} (mmHg)`}</div>
                  </div>
                )}
                {eco?.refluxoPAO?.length > 0 && (
                  <>
                    {eco.refluxoPAO.map((refluxoPAO, index) => (
                      <div key={`refluxoPAO-${index}`} className="row">
                        <div className="col-12"> - {refluxoPAO.refluxoPAO}</div>
                      </div>
                    ))}
                    <br />
                  </>
                )}
                {/* 11 */}
                {/* Quebra de página aqui */}
                {pageBreakC && (
                  <div className="page-break">
                    <ReportHeader printing={showTimbradoPrint} />
                  </div>
                )}
                {hasProteseTricuspide && (
                  <div className="row">
                    <div className="col-12">
                      <strong className="mr-auto">Prótese Tricúspide</strong>
                    </div>
                  </div>
                )}
                {!hasProteseTricuspide && hasValvaTricuspide && (
                  <>
                    <div className="row">
                      <div className="col-12">
                        <strong className="mr-auto">Valva Tricúspide</strong>
                      </div>
                    </div>
                  </>
                )}
                {/* 11-1 */}
                {eco?.GMVT != null && (
                  <div className="row">
                    <div className="col-12">{`- Gradiente Médio da Valva Tricúspide: ${eco.GMVT} (mmHg)`}</div>
                  </div>
                )}
                {eco?.estimativaPSVD != null && (
                  <div className="row">
                    <div className="col-12">{`- Estimativa da Pressão Sistólica da AP: ${eco.estimativaPSVD} (mmHg)`}</div>
                  </div>
                )}
                {eco.valvaTricu.map((valvaTricu, index) => (
                  <div key={`valvaTricu-${index}`} className="row">
                    <div className="col-12">- {valvaTricu.DESCRICAO}</div>
                  </div>
                ))}
                {eco?.refVT?.length > 0 && (
                  <>
                    {eco.refVT.map((refVT, index) => (
                      <div key={`refVT-${index}`} className="row">
                        <div className="col-12"> - {refVT.DESCRICAO}</div>
                      </div>
                    ))}
                    <br />
                  </>
                )}
                {/* 11-2 */}
                {eco?.GMPT && (
                  <div className="row">
                    <div className="col-12">
                      - Gradiente Médio da Prótese Tricúspide: {eco.GMPT} (mmHg)
                    </div>
                  </div>
                )}
                {eco?.refluxoPT?.map((item, index) => (
                  <div key={`refluxoPT-${index}`} className="row">
                    <div className="col-12">- {item.refluxoPT}</div>
                  </div>
                ))}
                {/* 12 */}
                {hasProtesePulmonar && (
                  <div className="row">
                    <div className="col-12">
                      <strong className="mr-auto">Prótese Pulmonar</strong>
                    </div>
                  </div>
                )}
                {!hasProtesePulmonar && hasValvaPulmonar && (
                  <div className="row">
                    <div className="col-12">
                      <strong className="mr-auto">Valva Pulmonar</strong>
                    </div>
                  </div>
                )}
                {eco?.DAVP && (
                  <div className="row">
                    <div className="col-12">
                      - Diâmetro do Anel Pulmonar: {Number(eco.DAVP).toFixed(1)}{' '}
                      (cm)
                    </div>
                  </div>
                )}
                {/* 12-1 */}
                {(eco?.GMVP !== null || eco?.refVP?.length > 0) && (
                  <>
                    {eco?.GMVP !== null && (
                      <div className="row">
                        <div className="col-12">
                          - Gradiente Médio da Valva Pulmonar: {eco.GMVP} (mmHg)
                        </div>
                      </div>
                    )}
                    {eco?.valvaPulmo?.map((valvaPulmo, index) => (
                      <div key={`valvaPulmo-${index}`} className="row">
                        <div className="col-12">- {valvaPulmo.DESCRICAO}</div>
                      </div>
                    ))}{' '}
                    {eco?.refVP?.map((ref, index) => (
                      <div key={`refVP-${index}`} className="row">
                        <div className="col-12">- {ref.DESCRICAO}</div>
                      </div>
                    ))}
                    <br />
                  </>
                )}
                {/* 12-2 */}
                {eco?.GMPP !== null && (
                  <div className="row">
                    <div className="col-12">
                      - Gradiente Médio da Prótese Pulmonar: {eco.GMPP} (mmHg)
                    </div>
                  </div>
                )}
                {eco?.refluxoPP?.map(refluxo => (
                  <div key={`refluxoPP-${refluxo.refluxoPP}`} className="row">
                    <div className="col-12">- {refluxo.refluxoPP}</div>
                  </div>
                ))}
                {/* 13 */}
                {eco?.pericardio?.length > 0 && (
                  <>
                    <div className="row">
                      <div className="col-12">
                        <strong className="mr-auto">Pericárdio</strong>
                      </div>
                    </div>

                    {eco.pericardio.map(({ DESCRICAO }) => (
                      <div key={`pericardio-${DESCRICAO}`} className="row">
                        <div className="col-12">- {DESCRICAO}</div>
                      </div>
                    ))}

                    <br />
                  </>
                )}
                {/* 14 */}
                {eco?.veiaCavaInf?.length > 0 && (
                  <>
                    <strong className="mr-auto">Veia Cava Inferior</strong>
                    {eco.veiaCavaInf.map((item, index) => (
                      <div key={`veiaCavaInf-${index}`} className="row">
                        <div className="col-12">- {item.DESCRICAO}</div>
                      </div>
                    ))}
                    <br />
                  </>
                )}
                {/* 15 */}
                {eco?.aorta?.length > 0 && (
                  <>
                    <strong className="mr-auto">Aorta</strong>
                    {eco.aorta.map(({ DESCRICAO }) => (
                      <div key={`aorta-${DESCRICAO}`} className="row">
                        <div className="col-12">- {DESCRICAO}</div>
                      </div>
                    ))}
                    <br />
                  </>
                )}
                {/* 16 */}
                {eco?.OBSERVACOES?.trim() && (
                  <>
                    <strong className="mr-auto">Observações</strong>
                    <pre className={styles.preContent} style={{ fontSize: 17 }}>
                      - {eco.OBSERVACOES.trim()}
                    </pre>
                  </>
                )}
                <strong className="mr-auto">Conclusões</strong>
                {eco?.valvulopatia?.length > 0 && (
                  <>
                    {eco?.valvulopatia?.map(valvu => (
                      <div
                        key={`valvulopatia-${valvu.valvulopatia}`}
                        className="row"
                      >
                        <div className="col-12"> - {valvu.valvulopatia}</div>
                      </div>
                    ))}
                  </>
                )}
                {eco?.protese?.length > 0 && (
                  <>
                    {eco?.protese?.map(prot => (
                      <div key={`protese-${prot.protese}`} className="row">
                        <div className="col-12"> - {prot.protese}</div>
                      </div>
                    ))}
                  </>
                )}
                {eco?.contraSeg?.length > 0 && (
                  <>
                    {eco?.contraSeg?.map(contra => (
                      <div
                        key={`contraSeg-${contra.contraSeg}`}
                        className="row"
                      >
                        <div className="col-12"> - {contra.contraSeg}</div>
                      </div>
                    ))}
                  </>
                )}
                {eco?.conclusoes?.length > 0 && (
                  <>
                    {eco?.conclusoes?.map(conclu => (
                      <div
                        key={`conclusoes-${conclu.conclusoes}`}
                        className="row"
                      >
                        <div className="col-12"> - {conclu.conclusoes}</div>
                      </div>
                    ))}
                  </>
                )}
              </div>
            ) : (
              <div id="normal" ref={normalRef} style={laudoFontStyle}>
                <strong className="mr-auto">
                  Classificar Laudo como Alterado. Consulte a Administração do
                  ICM.
                </strong>
              </div>
            )}
          </Card.Body>

          <Card.Footer className="text-muted">
            {eco?.CODCONV === 29 || eco?.CODCONV === 804
              ? null
              : urls.length > 0 && (
                <div className={styles.messageReport}>
                  Imagens disponíveis no site:{' '}
                  <strong>www.icm.com.br (Resultado de Exames)</strong>
                  {/* TEMPORÁRIO */}
                  <br />
                  <span>Código de verificação: {eco?.CODPAC}</span>
                  <hr></hr>
                </div>
              )}
            <div className={styles.signatureReport}>
              <i className="mr-auto">
                Conferido e assinado eletronicamente por:{' '}
              </i>
              <strong className="mr-auto">
                {getPrefix(eco?.CODREALIZOU)} {eco?.Realizou} - CRM:{' '}
                {eco?.CRMMED} - RQE: {eco?.RQE}
              </strong>
              <br></br>
              <i className="mr-auto">
                Assinatura Digital: {md5(JSON.stringify(eco))}
              </i>
            </div>
            <div className="text-center removeMePrint">
              {!isMedicoExterno && (
                <>
                  <Button
                    variant={
                      isTimbradoPrint ? 'outline-secondary' : 'secondary'
                    }
                    onClick={() => setIsTimbradoPrint(false)}
                    aria-label="Imprimir sem timbrado"
                  >
                    Sem Timbrado
                  </Button>{' '}
                  <Button
                    variant={
                      isTimbradoPrint ? 'secondary' : 'outline-secondary'
                    }
                    onClick={() => setIsTimbradoPrint(true)}
                    aria-label="Imprimir com timbrado"
                  >
                    Com Timbrado
                  </Button>{' '}
                  <Button
                    variant={
                      isTimbradoPrint ? 'outline-success' : 'success'
                    }
                    onClick={() => generateDopplerPDF(eco, 'imprimirTimbrado')}
                    aria-label="Imprimir com timbrado"
                  >
                    Visualizar PDF
                  </Button>{' '}
                </>
              )}
              <Button
                onClick={handleRemoveDiv}
                aria-label="Imprimir laudo"
              >
                Imprimir
              </Button>{' '}
              {!isMedicoExterno && (
                <>
                  <Button
                    variant={
                      isResumoConvenio ? 'secondary' : 'outline-secondary'
                    }
                    onClick={() => setIsResumoConvenio(prev => !prev)}
                    aria-label={
                      isResumoConvenio
                        ? 'Ver relatório completo'
                        : 'Ver relatório resumido'
                    }
                  >
                    {isResumoConvenio
                      ? 'Ver Relatório Completo'
                      : 'Ver Relatório Resumido'}
                  </Button>{' '}
                </>
              )}
              <Button
                variant="success"
                onClick={() => loadFiles({ openModal: true })}
                aria-label="Ver imagens do laudo"
              >
                Ver imagens
              </Button>
            </div>
          </Card.Footer>
        </div>
      </Card>

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Imagens</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingFiles ? (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '50vh',
              }}
            >
              <Spinner animation="border" variant="dark" size="lg" />
            </div>
          ) : (
            <FileCarousel urls={urls} setURLs={setURLs} />
          )}
        </Modal.Body>
      </Modal>
    </>
  );
}
